import { Image } from 'expo-image';
import { Href, router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { heartEmpty, npoIcon, userFill } from '@/assets/images/icons';
import GradientCircularButton from '@/components/buttons/GradientCircularButton';
import Card from '@/components/Card';
import LoginHeader from '@/components/LoginHeader';
import Text from '@/components/Text';
import TRBShowBanner from '@/components/trbShow/TRBShowBanner';
import PlatformContainer from '@/components/web/PlatformContainer';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/constants/config';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';
import { actuatedNormalizeHorizontal, actuatedNormalizeVertical } from '@/utils/pixelScaling';

export const onPressPlatform = (href: Href, url: string) => {
  if (Platform.OS === 'web') router.navigate(href);
  else WebBrowser.openBrowserAsync(url);
};

export const onPressCondition = (termType: 'tos' | 'pp') => {
  if (termType === 'tos') onPressPlatform('/termsOfService', TERMS_OF_USE_URL);
  else onPressPlatform('/privacyPolicy', PRIVACY_POLICY_URL);
};

const OnBoardingScreen = () => {
  const colors = useColor();
  const [gradientWidth, setGradientWidth] = useState(92);

  //Locales
  const subtitle = i18n.t('sign_in.onboarding.subtitle');
  const npoTitle = i18n.t('sign_in.onboarding.npo_title');
  const npoDescription = i18n.t('sign_in.onboarding.npo_description');
  const individualTitle = i18n.t('sign_in.onboarding.individual_title');
  const browseTitle = i18n.t('sign_in.onboarding.browse_title');
  const privacyPolicy = i18n.t('settings.screen_names.privacy_policy');
  const termsOfService = i18n.t('mix.terms_of_service');

  //Press handlers
  const onPressUser = () => {
    router.navigate('/(app)/login/individual');
  };
  const onPressNpo = () => {
    router.navigate('/(app)/login/nonProfit');
  };
  const onPressBrowse = () => {
    router.navigate('/home/npoTabs');
  };

  return (
    <PlatformContainer contentCustomClassnames="flex">
      <SafeAreaView style={styles.flexOne}>
        <View style={styles.container}>
          <LoginHeader />
          <Text style={[styles.subtitle, { color: colors.titleText }]} fontWeight="700">
            {subtitle}
          </Text>
          <View style={styles.buttonsContainer}>
            <Card
              style={styles.cardContent}
              onPress={onPressNpo}
              activeOpacity={0.5}
              onLayout={(event) => setGradientWidth(event.nativeEvent.layout.height - 20)}>
              <GradientCircularButton disabled size={gradientWidth}>
                <Image source={npoIcon} style={styles.npoIcon} contentFit="contain" />
              </GradientCircularButton>
              <View style={styles.cardTextContent}>
                <View style={styles.npoCardTextContainer}>
                  <Text
                    fontWeight="900"
                    style={[styles.cardTitle, { color: colors.titleText }]}
                    numberOfLines={1}
                    ellipsizeMode="tail">
                    {npoTitle}
                  </Text>
                  <Text fontWeight="450" style={[styles.cardDescription, { color: colors.titleText }]}>
                    {npoDescription}
                  </Text>
                </View>
              </View>
            </Card>
            <Card style={styles.cardContent} onPress={onPressUser} activeOpacity={0.5}>
              <GradientCircularButton disabled size={gradientWidth}>
                <Image source={userFill} style={styles.userFillIcon} contentFit="contain" tintColor={'black'} />
              </GradientCircularButton>
              <View style={styles.cardTextContent}>
                <Text fontWeight="900" style={[styles.cardTitle, { color: colors.titleText }]} numberOfLines={1}>
                  {individualTitle}
                </Text>
              </View>
            </Card>
            <Card style={styles.cardContent} onPress={onPressBrowse} activeOpacity={0.5}>
              <GradientCircularButton disabled size={gradientWidth}>
                <Image source={heartEmpty} style={styles.heartEmptyIcon} contentFit="contain" tintColor={'black'} />
              </GradientCircularButton>
              <View style={styles.cardTextContent}>
                <Text fontWeight="900" style={[styles.cardTitle, { color: colors.titleText }]} numberOfLines={1}>
                  {browseTitle}
                </Text>
              </View>
            </Card>
            <TRBShowBanner />
          </View>
          <View style={styles.conditions}>
            <Text
              fontWeight="700"
              style={[styles.conditionText, { color: colors.linkText }]}
              onPress={() => onPressCondition('pp')}>
              {privacyPolicy}
            </Text>
            <Text
              fontWeight="700"
              style={[styles.conditionText, { color: colors.linkText }]}
              onPress={() => onPressCondition('tos')}>
              {termsOfService}
            </Text>
          </View>
        </View>
      </SafeAreaView>
    </PlatformContainer>
  );
};

export default OnBoardingScreen;

const styles = StyleSheet.create({
  buttonsContainer: {
    flexDirection: 'column',
    flex: 1
  },

  container: {
    paddingHorizontal: actuatedNormalizeHorizontal(18),
    flex: 1
  },

  npoIcon: {
    height: '47%',
    width: '47%'
  },

  userFillIcon: {
    height: '55%',
    width: '55%'
  },

  heartEmptyIcon: {
    height: '45%',
    width: '45%'
  },

  cardContent: {
    paddingHorizontal: actuatedNormalizeHorizontal(20),
    flex: 1,
    flexDirection: 'row',
    marginBottom: actuatedNormalizeVertical(25),
    paddingVertical: actuatedNormalizeVertical(10),
    maxHeight: actuatedNormalizeVertical(112)
  },

  cardTextContent: {
    marginLeft: actuatedNormalizeHorizontal(17),
    flex: 1,
    alignItems: 'flex-start'
  },

  npoCardTextContainer: {
    alignItems: 'center',
    maxWidth: '100%'
  },

  cardTitle: {
    fontSize: actuatedNormalizeHorizontal(20),
    overflow: 'hidden'
  },

  cardDescription: {
    fontSize: actuatedNormalizeHorizontal(14),
    textAlign: 'center'
  },

  subtitle: {
    fontSize: actuatedNormalizeHorizontal(22),
    marginBottom: actuatedNormalizeVertical(25)
  },

  conditions: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: actuatedNormalizeVertical(25),
    paddingVertical: 20
  },

  conditionLink: {
    alignSelf: 'center'
  },

  conditionText: {
    fontSize: actuatedNormalizeHorizontal(14)
  },

  flexOne: {
    flex: 1
  }
});
